import { create } from 'zustand';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import { useToastStore } from '@/stores/toastStore';

export type PlanId = 'free' | 'pro';

export interface PlanFeatures {
  zoomConnect: boolean;
  accountAnswers: boolean;
  healthCheck: boolean;
  licenseReport: boolean;
  nlConfig: boolean;
  bulkChanges: boolean;
  auditTrail: boolean;
  unlimitedAdminUsers: boolean;
}

export interface PaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  status: string;
  pdfUrl: string | null;
  hostedUrl: string | null;
}

interface BillingState {
  /** false while the backend has billing switched off: every feature is available, no checkout. */
  billingEnabled: boolean;
  plan: PlanId;
  subscriptionStatus: string | null;
  seats: number;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
  features: PlanFeatures;
  isLoading: boolean;
  paymentMethod: PaymentMethod | null;
  invoices: Invoice[];

  // Actions
  fetchStatus: () => Promise<void>;
  verifySession: (sessionId: string) => Promise<boolean>;
  createCheckout: (seats: number, annual: boolean) => Promise<string | null>;
  updateSeats: (seats: number) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  fetchPaymentMethod: () => Promise<void>;
  createSetupIntent: () => Promise<string | null>;
  updateDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  fetchInvoices: () => Promise<void>;
  isPro: () => boolean;
  isTrialing: () => boolean;
  daysLeftInTrial: () => number | null;
}

const FREE_FEATURES: PlanFeatures = {
  zoomConnect: false,
  accountAnswers: false,
  healthCheck: false,
  licenseReport: false,
  nlConfig: false,
  bulkChanges: false,
  auditTrail: false,
  unlimitedAdminUsers: false,
};

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const resp = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      'X-Tenant-ID': '',
      ...options?.headers,
    },
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.message || `Request failed: ${resp.status}`);
  }
  return resp.json();
}

export const useBillingStore = create<BillingState>((set, get) => ({
  // Assume billing is enforced until /billing/status says otherwise.
  billingEnabled: true,
  plan: 'free',
  subscriptionStatus: null,
  seats: 0,
  currentPeriodEnd: null,
  trialEnd: null,
  cancelAtPeriodEnd: false,
  features: FREE_FEATURES,
  isLoading: false,
  paymentMethod: null,
  invoices: [],

  fetchStatus: async () => {
    set({ isLoading: true });
    try {
      const data = await apiFetch<{
        billingEnabled: boolean;
        plan: PlanId;
        subscriptionStatus: string | null;
        seats: number;
        currentPeriodEnd: string | null;
        trialEnd: string | null;
        cancelAtPeriodEnd: boolean;
        features: PlanFeatures;
      }>('/billing/status');

      set({
        billingEnabled: data.billingEnabled,
        plan: data.plan,
        subscriptionStatus: data.subscriptionStatus,
        seats: data.seats,
        currentPeriodEnd: data.currentPeriodEnd,
        trialEnd: data.trialEnd,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd,
        features: data.features,
      });
    } catch {
      // Default to free on error
      set({ plan: 'free', cancelAtPeriodEnd: false, features: FREE_FEATURES });
    } finally {
      set({ isLoading: false });
    }
  },

  verifySession: async (sessionId: string) => {
    try {
      const data = await apiFetch<{
        activated: boolean;
        plan: PlanId;
        subscriptionStatus: string | null;
      }>('/billing/verify-session', {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      });

      if (data.activated) {
        await get().fetchStatus();
      }
      return data.activated;
    } catch {
      return false;
    }
  },

  createCheckout: async (seats: number, annual: boolean) => {
    try {
      const data = await apiFetch<{ clientSecret: string }>('/billing/checkout/embedded', {
        method: 'POST',
        body: JSON.stringify({
          seats,
          annual,
          returnUrl: `${window.location.origin}/settings?billing=success&session_id={CHECKOUT_SESSION_ID}`,
        }),
      });
      return data.clientSecret;
    } catch (err) {
      useToastStore.getState().addToast(
        err instanceof Error ? err.message : 'Failed to start checkout. Please try again.',
        'error',
      );
      return null;
    }
  },

  updateSeats: async (seats: number) => {
    try {
      await apiFetch('/billing/update-seats', {
        method: 'POST',
        body: JSON.stringify({ seats }),
      });
      await get().fetchStatus();
      useToastStore.getState().addToast(`Seat count updated to ${seats}.`, 'success');
    } catch (err) {
      useToastStore.getState().addToast(err instanceof Error ? err.message : 'Failed to update seats.', 'error');
      throw err;
    }
  },

  cancelSubscription: async () => {
    try {
      await apiFetch('/billing/cancel', { method: 'POST' });
      await get().fetchStatus();
      const periodEnd = get().currentPeriodEnd;
      const dateStr = periodEnd ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'period end';
      useToastStore.getState().addToast(`Subscription cancelled. Access continues until ${dateStr}.`, 'success');
    } catch (err) {
      useToastStore.getState().addToast(err instanceof Error ? err.message : 'Failed to cancel subscription.', 'error');
      throw err;
    }
  },

  reactivateSubscription: async () => {
    try {
      await apiFetch('/billing/reactivate', { method: 'POST' });
      await get().fetchStatus();
      useToastStore.getState().addToast('Subscription reactivated!', 'success');
    } catch (err) {
      useToastStore.getState().addToast(err instanceof Error ? err.message : 'Failed to reactivate subscription.', 'error');
      throw err;
    }
  },

  fetchPaymentMethod: async () => {
    try {
      const data = await apiFetch<{ paymentMethod: PaymentMethod | null }>('/billing/payment-method');
      set({ paymentMethod: data.paymentMethod });
    } catch {
      set({ paymentMethod: null });
    }
  },

  createSetupIntent: async () => {
    try {
      const data = await apiFetch<{ clientSecret: string }>('/billing/setup-intent', {
        method: 'POST',
      });
      return data.clientSecret;
    } catch {
      return null;
    }
  },

  updateDefaultPaymentMethod: async (paymentMethodId: string) => {
    try {
      await apiFetch('/billing/default-payment-method', {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
      });
      await get().fetchPaymentMethod();
      useToastStore.getState().addToast('Payment method updated.', 'success');
    } catch (err) {
      useToastStore.getState().addToast(err instanceof Error ? err.message : 'Failed to update payment method.', 'error');
      throw err;
    }
  },

  fetchInvoices: async () => {
    try {
      const data = await apiFetch<{ invoices: Invoice[] }>('/billing/invoices');
      set({ invoices: data.invoices });
    } catch (err) {
      set({ invoices: [] });
      throw err;
    }
  },

  isPro: () => {
    const { billingEnabled, plan, subscriptionStatus } = get();
    if (!billingEnabled) return true; // no billing → everyone has the full product
    return plan === 'pro' && subscriptionStatus !== 'canceled';
  },

  isTrialing: () => {
    return get().subscriptionStatus === 'trialing';
  },

  daysLeftInTrial: () => {
    const { trialEnd, subscriptionStatus } = get();
    if (subscriptionStatus !== 'trialing' || !trialEnd) return null;
    const diff = new Date(trialEnd).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  },
}));
